import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Calendar, Linkedin, Mail, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { API } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("id-ID", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso; }
};

const MD_COMPONENTS = {
  h1: ({node, ...p}) => <h2 className="mt-10 mb-4 text-2xl sm:text-3xl text-white font-medium tracking-tight" style={{ fontFamily: "Outfit" }} {...p} />,
  h2: ({node, ...p}) => <h3 className="mt-9 mb-3 text-xl sm:text-2xl text-white font-medium" style={{ fontFamily: "Outfit" }} {...p} />,
  h3: ({node, ...p}) => <h4 className="mt-7 mb-2 text-lg text-white font-medium" style={{ fontFamily: "Outfit" }} {...p} />,
  p:  ({node, ...p}) => <p className="my-5 text-slate-300 leading-relaxed text-[16.5px]" {...p} />,
  a:  ({node, ...p}) => <a className="text-blue-300 hover:text-blue-200 underline decoration-blue-400/40 underline-offset-4" target="_blank" rel="noopener noreferrer" {...p} />,
  ul: ({node, ...p}) => <ul className="my-5 ml-5 list-disc text-slate-300 space-y-2 marker:text-blue-400/70" {...p} />,
  ol: ({node, ...p}) => <ol className="my-5 ml-5 list-decimal text-slate-300 space-y-2 marker:text-blue-400/70" {...p} />,
  li: ({node, ...p}) => <li className="leading-relaxed" {...p} />,
  blockquote: ({node, ...p}) => (
    <blockquote className="my-6 border-l-2 border-blue-400/50 pl-5 italic text-slate-300 bg-blue-500/[0.03] py-3 pr-4 rounded-r-lg" {...p} />
  ),
  code: ({inline, className, children, ...rest}) =>
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-white/[0.06] text-blue-200 text-[0.9em] font-mono-display" {...rest}>{children}</code>
    ) : (
      <pre className="my-6 p-4 rounded-xl bg-[#0a112a] border border-white/[0.06] overflow-x-auto">
        <code className="text-sm text-slate-200 font-mono-display" {...rest}>{children}</code>
      </pre>
    ),
  hr: () => <hr className="my-10 border-white/[0.06]" />,
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [seo, setSeo] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const [a, s, list] = await Promise.all([
          axios.get(`${API}/articles/${slug}`).then((r) => r.data),
          axios.get(`${API}/articles/${slug}/seo`).then((r) => r.data).catch(() => null),
          axios.get(`${API}/articles?limit=20`).then((r) => r.data).catch(() => []),
        ]);
        if (!mounted) return;
        setArticle(a);
        setSeo(s);
        setRelated(
          (list || [])
            .filter((x) => x.slug !== slug && x.category === a.category)
            .slice(0, 3),
        );
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => { mounted = false; };
  }, [slug]);

  // SEO meta injection
  useSeo(
    article
      ? {
          title: seo?.title || article.title,
          description: seo?.description || article.excerpt,
          image: seo?.og_image || (typeof window !== "undefined" ? `${window.location.origin}/api/og/${slug}.png` : ""),
          url: seo?.url || (typeof window !== "undefined" ? `${window.location.origin}/blog/${slug}` : ""),
          type: "article",
          category: article.category,
          publishedAt: article.published_at,
          keywords: seo?.keywords?.length ? seo.keywords : [article.category, "Joneten Saputra", "Product Manager"],
          author: article.author,
        }
      : { title: "Blog" },
  );

  const handleShare = async (target) => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (target === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link disalin");
      } catch {
        toast.error("Gagal menyalin link");
      }
    } else if (target === "twitter") {
      const text = encodeURIComponent(article?.title || "");
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, "_blank");
    } else if (target === "linkedin") {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
    }
  };

  return (
    <div className="relative min-h-screen bg-[#060a1a] text-slate-200" data-testid="blog-detail-page">
      <Navbar />

      <main className="relative pt-28 pb-20">
        <div className="absolute inset-0 grid-bg-fine opacity-25 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-blue-500/10 rounded-full blur-[150px] pointer-events-none" />

        {loading ? (
          <div className="relative max-w-3xl mx-auto px-6 py-16 text-sm text-slate-500 font-mono-display" data-testid="article-loading">
            loading article…
          </div>
        ) : notFound ? (
          <div className="relative max-w-3xl mx-auto px-6 py-20 text-center" data-testid="article-not-found">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400 font-mono-display">
              /// 404
            </div>
            <h1 className="mt-4 text-3xl text-white" style={{ fontFamily: "Outfit" }}>
              Artikel tidak ditemukan.
            </h1>
            <p className="mt-3 text-slate-400">URL artikel mungkin sudah berubah atau dihapus.</p>
            <button
              onClick={() => navigate("/#blog")}
              className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Kembali ke daftar artikel
            </button>
          </div>
        ) : (
          <article className="relative max-w-3xl mx-auto px-6">
            {/* Back */}
            <Link
              to="/#blog"
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="article-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Semua artikel
            </Link>

            {/* Category */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display"
              data-testid="article-category"
            >
              <span className="w-6 h-px bg-blue-400" />
              {article.category}
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl text-white font-medium tracking-tight leading-[1.1]"
              style={{ fontFamily: "Outfit" }}
              data-testid="article-title"
            >
              {article.title}
            </motion.h1>

            {/* Excerpt */}
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-5 text-lg text-slate-300 leading-relaxed"
            >
              {article.excerpt}
            </motion.p>

            {/* Meta */}
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500 font-mono-display border-y border-white/[0.06] py-4">
              <span className="inline-flex items-center gap-2 text-slate-300">
                <span className="w-6 h-6 rounded-full bg-blue-500/15 border border-blue-400/30 grid place-items-center text-[10px]">
                  J
                </span>
                {article.author || "Joneten Saputra"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" strokeWidth={1.75} />
                {formatDate(article.published_at)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.75} />
                {article.read_time}
              </span>
              <div className="ml-auto inline-flex items-center gap-1">
                <ShareBtn icon={Share2} onClick={() => handleShare("copy")} title="Copy link" testid="share-copy" />
                <ShareBtn icon={Twitter} onClick={() => handleShare("twitter")} title="Share on X" testid="share-twitter" />
                <ShareBtn icon={Linkedin} onClick={() => handleShare("linkedin")} title="Share on LinkedIn" testid="share-linkedin" />
              </div>
            </div>

            {/* Hero cover */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="mt-10 relative rounded-2xl overflow-hidden border border-white/[0.06] aspect-[1200/630] glass-elevated"
              data-testid="article-cover"
            >
              <img
                src={`${API}/og/${slug}.png`}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </motion.div>

            {/* Content */}
            <div className="mt-12 prose-content" data-testid="article-content">
              {article.content ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={MD_COMPONENTS}
                >
                  {article.content}
                </ReactMarkdown>
              ) : (
                <p className="text-slate-500 italic text-sm">
                  Konten belum tersedia. Artikel ini akan segera diperbarui.
                </p>
              )}
            </div>

            {/* Tags / keywords */}
            {seo?.keywords?.length ? (
              <div className="mt-12 flex flex-wrap gap-2" data-testid="article-keywords">
                {seo.keywords.map((k) => (
                  <span key={k} className="px-3 py-1 rounded-full text-xs border border-white/10 bg-white/[0.03] text-slate-300 font-mono-display">
                    #{k}
                  </span>
                ))}
              </div>
            ) : null}

            {/* Author card */}
            <aside className="mt-16 rounded-2xl p-6 glass-elevated corners flex items-start gap-5" data-testid="author-card">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 grid place-items-center text-white font-medium" style={{ fontFamily: "Outfit" }}>
                JS
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-blue-400 font-mono-display">written by</div>
                <div className="mt-1 text-white font-medium" style={{ fontFamily: "Outfit" }}>Joneten Saputra</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                  Product Manager &amp; Technical Solution Specialist berpengalaman sejak 2012 dalam AI, IoT,
                  software, hardware, dan smart energy.
                </p>
                <div className="mt-3 flex gap-2">
                  <a href="mailto:joneten91@gmail.com" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-blue-300 px-3 py-1.5 rounded-full border border-white/10 transition-colors">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                  <a href="https://www.linkedin.com/in/joneten-saputra-913816105/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-blue-300 px-3 py-1.5 rounded-full border border-white/10 transition-colors">
                    <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                  </a>
                </div>
              </div>
            </aside>

            {/* Related */}
            {related.length > 0 && (
              <div className="mt-16" data-testid="related-articles">
                <div className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display mb-5">
                  /// related
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/blog/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="group rounded-2xl p-5 glass-elevated corners hover:-translate-y-0.5 hover:border-blue-400/40 transition-all"
                    >
                      <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300 font-mono-display">
                        {r.category}
                      </div>
                      <div className="mt-2 text-white font-medium leading-snug group-hover:text-gradient-aurora" style={{ fontFamily: "Outfit" }}>
                        {r.title}
                      </div>
                      <div className="mt-3 text-[11px] text-slate-500 font-mono-display flex items-center gap-3">
                        <span>{formatDate(r.published_at)}</span>
                        <span>{r.read_time}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
};

const ShareBtn = ({ icon: Icon, onClick, title, testid }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className="p-1.5 rounded-lg text-slate-400 hover:text-blue-300 hover:bg-white/[0.05] transition-colors"
  >
    <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
  </button>
);

export default BlogDetail;
